import { environment } from "@/../environments/environment";
import { ActionDoneType } from "@/enums/ActionDoneType.enum";
import { ObjectUpdatedType } from "@/enums/ObjectUpdatedType.enum";
import { AppConstants } from "@/helpers/app.constants";
import { Injectable } from "@angular/core";
import {
  NavigationEnd,
  NavigationStart,
  Params,
  Router,
} from "@angular/router";
import {
  Application,
  CertificationRequest,
  Connector,
  DocumentCategory,
  RealProperty,
  UserPreferences,
} from "@eemo/api-client";
import { BROWSERS, DeviceDetectorService, OS } from "ngx-device-detector";
import { BehaviorSubject, Observable, Subject } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AppConfigService {
  private envType: string;

  private https: boolean;

  private _showIntro = false;

  private _selectedProp: RealProperty;
  private _selectPropSubject = new BehaviorSubject<RealProperty>(null);
  private _selectedPropCertificationRequestSubject =
    new BehaviorSubject<CertificationRequest>(null);
  private _userReachedPropertyLimitSubject = new BehaviorSubject<boolean>(
    false
  );

  private _mainPhotoUpdatedSubject = new BehaviorSubject<boolean>(null);

  private _currentDocCategory: DocumentCategory;
  private _currentDocCategorySubject = new BehaviorSubject<DocumentCategory>(
    DocumentCategory.BUILDING
  );

  private _availableConnectorsSubject = new BehaviorSubject<Connector[]>(null);

  private _userPreferencesSubject = new BehaviorSubject<UserPreferences>(null);

  private _objectUpdatedSubject = new Subject<ObjectUpdatedType>();
  private _actionDoneSubject = new Subject<ActionDoneType>();

  private _floatMenuEnabledSubject = new BehaviorSubject<boolean>(true);

  private _os: string;
  private _isDesktop: boolean;
  private _isMobileWeb: boolean;
  private _isTabletWeb: boolean;
  private _isMobileApp: boolean = localStorage.getItem(
    AppConstants.PARAM_IS_MOBILE_APP
  )
    ? true
    : false;
  private _applicationType: Application = Application.DESKTOP;

  constructor(
    private router: Router,
    private deviceDetectorService: DeviceDetectorService
  ) {
    switch (window.location.hostname) {
      case "localhost":
      case environment.devHostname:
        this.envType = "DEV";
        break;
      case environment.demoHostname:
        this.envType = "DEMO";
        break;
      default:
        this.envType = "PROD";
    }
    switch (window.location.protocol) {
      case "http:":
        this.https = false;
        break;
      default:
        this.https = true;
    }

    this.router.events.subscribe({
      next: (e) => {
        if (e instanceof NavigationStart) {
          if (this.isPropertyPage()) {
            // at start we remove the old selected prop if the destination is the property page (will be reloaded by it)
            // this._selectPropSubject.next(null);
          }
        }
        if (e instanceof NavigationEnd) {
          if (!this.isPropertyPage()) {
            // at end we remove the selected prop if the destination is not the property page
            this._selectPropSubject.next(null);
          }
        }
      },
    });

    // manage local stored connectors
    const connectorsCache = localStorage.getItem(
      AppConstants.STORAGE_KEY_CONNECTORS_AVAILABLE
    );
    if (connectorsCache) {
      this._availableConnectorsSubject.next(JSON.parse(connectorsCache));
    }

    // manage local user preferences
    const preferencesCache = localStorage.getItem(
      AppConstants.STORAGE_KEY_USER_PREFERENCES
    );
    if (preferencesCache) {
      this._userPreferencesSubject.next(JSON.parse(preferencesCache));
    }

    // bug ipad are detected as desktop
    this._isDesktop =
      deviceDetectorService.isDesktop() && !deviceDetectorService.isTablet();
    this._isMobileWeb = deviceDetectorService.isMobile();
    this._isTabletWeb = deviceDetectorService.isTablet();
  }

  get isDesktop(): boolean {
    return this._isDesktop;
  }

  get isMobileWeb(): boolean {
    return this._isMobileWeb;
  }

  get isTabletWeb(): boolean {
    return this._isTabletWeb;
  }

  set isMobileApp(isMobileApp: boolean) {
    this._isMobileApp = isMobileApp;
    if (isMobileApp) {
      localStorage.setItem(AppConstants.PARAM_IS_MOBILE_APP, "true");
    } else {
      localStorage.removeItem(AppConstants.PARAM_IS_MOBILE_APP);
    }
  }

  get isMobileApp(): boolean {
    return this._isMobileApp;
  }

  get isMobileOrTablet(): boolean {
    return !this._isDesktop;
  }

  get isOSiOS(): boolean {
    // note : ipad OS is detected as Mac
    return this._os === OS.IOS || (this.isTabletWeb && this._os === OS.MAC);
  }

  get isOSAndroid(): boolean {
    // FIXME Sometimes android device are detected as OTHER
    return (
      this._os === OS.ANDROID || this.applicationType === Application.OTHER
    );
  }

  get isBrowserSafari() {
    return this.deviceDetectorService.browser === BROWSERS.SAFARI;
  }

  get applicationType(): Application {
    return this._applicationType;
  }

  get showIntro(): boolean {
    return this._showIntro;
  }

  set showIntro(show: boolean) {
    this._showIntro = show;
  }

  get selectedPropObs(): Observable<RealProperty> {
    return this._selectPropSubject.asObservable();
  }

  get selectedProp(): RealProperty {
    return this._selectedProp;
  }

  set selectedProp(prop: RealProperty) {
    this._selectedProp = prop;
    this._selectedPropCertificationRequestSubject.next(null);
    this._selectPropSubject.next(prop);
  }

  get selectedPropCertificationRequestObs(): Observable<CertificationRequest> {
    return this._selectedPropCertificationRequestSubject.asObservable();
  }

  set selectedPropCertificationRequest(request: CertificationRequest) {
    this._selectedPropCertificationRequestSubject.next(request);
  }

  get userReachedPropertyLimitObs(): Observable<boolean> {
    return this._userReachedPropertyLimitSubject.asObservable();
  }

  set userReachedPropertyLimit(enabled: boolean) {
    this._userReachedPropertyLimitSubject.next(enabled);
  }

  get floatMenuEnabledObs(): Observable<boolean> {
    return this._floatMenuEnabledSubject.asObservable();
  }

  set floatMenuEnabled(enabled: boolean) {
    this._floatMenuEnabledSubject.next(enabled);
  }

  get mainPhotoUpdated(): Observable<boolean> {
    return this._mainPhotoUpdatedSubject.asObservable();
  }

  get objectUpdated(): Observable<ObjectUpdatedType> {
    return this._objectUpdatedSubject.asObservable();
  }

  get actionDone(): Observable<ActionDoneType> {
    return this._actionDoneSubject.asObservable();
  }

  get currentDocCategoryObs(): Observable<DocumentCategory> {
    return this._currentDocCategorySubject.asObservable();
  }

  get currentDocCategory(): DocumentCategory {
    return this._currentDocCategory;
  }

  set currentDocCategory(category: DocumentCategory) {
    this._currentDocCategory = category;
    this._currentDocCategorySubject.next(category);
  }

  get availableConnectorsObs(): Observable<Connector[]> {
    return this._availableConnectorsSubject.asObservable();
  }

  set availableConnectors(connectors: Connector[]) {
    this._availableConnectorsSubject.next(connectors);
    localStorage.setItem(
      AppConstants.STORAGE_KEY_CONNECTORS_AVAILABLE,
      JSON.stringify(connectors)
    );
  }

  get userPreferencesObs(): Observable<UserPreferences> {
    return this._userPreferencesSubject.asObservable();
  }

  set userPreferences(preferences: UserPreferences) {
    this._userPreferencesSubject.next(preferences);
    localStorage.setItem(
      AppConstants.STORAGE_KEY_USER_PREFERENCES,
      JSON.stringify(preferences)
    );
  }

  get showAppDownload(): boolean {
    const showAppSaved = localStorage.getItem(
      AppConstants.STORAGE_KEY_SHOW_APP_DOWNLOAD
    ) as unknown as number;
    if (!showAppSaved || showAppSaved < Date.now()) {
      // show only for mobile & tablet
      return (this.isMobileWeb || this.isTabletWeb) && !this.isMobileApp;
    }
  }

  set showAppDownload(value: boolean) {
    if (value) {
      localStorage.removeItem(AppConstants.STORAGE_KEY_SHOW_APP_DOWNLOAD);
    } else {
      // disable for one week
      const ts = Date.now() + 1000 * 60 * 60 * 24 * 7;
      localStorage.setItem(
        AppConstants.STORAGE_KEY_SHOW_APP_DOWNLOAD,
        JSON.stringify(ts)
      );
    }
  }

  public static isParamMobileApp(params: Params): boolean {
    return params[AppConstants.PARAM_IS_MOBILE_APP] ? true : false;
  }

  detectApplicationType() {
    this._os = this.deviceDetectorService.os;
    let appType = Application.DESKTOP;
    if (this.isMobileApp) {
      switch (this.deviceDetectorService.os.toLowerCase()) {
        case OS.ANDROID.toLowerCase():
          appType = Application.ANDROID;
          break;
        case OS.IOS.toLowerCase():
        case OS.MAC.toLowerCase():
          // iPad is detected as Mac
          appType = Application.IOS;
          break;
        default:
          appType = Application.OTHER;
      }
    } else if (this.isMobileWeb) {
      appType = Application.MOBILE;
    }
    this._applicationType = appType;

    if (!environment.production) {
      console.log("OS", this.deviceDetectorService.os);
      console.log("isIOS", this.isOSiOS);
      console.log("isAndroid", this.isOSAndroid);
      console.log("isMobileApp", this.isMobileApp);
      console.log("isDesktop", this.isDesktop);
      console.log("isTabletWeb", this.isTabletWeb);
      console.log("isMobileWeb", this.isMobileWeb);
    }
  }

  private isPropertyPage(): boolean {
    const urlTab = this.router.routerState.snapshot.url.split("/");
    return urlTab.length < 1 || urlTab[1] === "property";
  }

  public isProd(): boolean {
    return this.envType === "PROD";
  }
  public isDemo(): boolean {
    return this.envType === "DEMO";
  }
  public isDev(): boolean {
    return this.envType === "DEV";
  }

  public isHttps(): boolean {
    return this.https;
  }

  triggerMainPhotoUpdated() {
    this._mainPhotoUpdatedSubject.next(true);
  }

  triggerObjectUpdated(type: ObjectUpdatedType) {
    this._objectUpdatedSubject.next(type);
  }

  triggerActionDone(type: ActionDoneType) {
    this._actionDoneSubject.next(type);
  }
}
